import axios from 'axios';
import type { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, getRefreshToken } from '@/auth/token-store';
import { refreshSingleFlight, onRefreshFailed } from '@/auth/refreshClient';

const API_BASE = import.meta.env.VITE_API_BASE_URL;

// Spring @ModelAttribute binds query params to a DTO's OWN flat field names (pageNumber,
// pageSize, key, postTypes, ...). orval wraps them in a single nested object param
// ({ requestDTO: {...} } / { searchRequestDTO: {...} }, because springdoc emitted the
// @ModelAttribute DTO as a nested object). axios' default serializer would emit bracket
// notation (requestDTO[pageNumber]=2) which @ModelAttribute ignores — so paging/filters
// silently fall back to DTO defaults (always page 1). Flatten one level to plain flat params.
export const serializeParams = (params: Record<string, unknown>): string => {
  const usp = new URLSearchParams();
  const append = (key: string, val: unknown) => {
    if (val === undefined || val === null) return;
    if (Array.isArray(val)) {
      val.forEach((v) => append(key, v)); // Spring list binding: repeated key
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-base-to-string -- primitives only here: arrays recurse above, objects are unwrapped by the caller
    usp.append(key, String(val));
  };
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      // unwrap the orval @ModelAttribute wrapper (requestDTO/searchRequestDTO) -> flat fields
      for (const [ik, iv] of Object.entries(v as Record<string, unknown>)) append(ik, iv);
    } else {
      append(k, v);
    }
  }
  return usp.toString();
};

// Canonical instance name per CONTRACTS.md §2 (NOT `AXIOS`).
export const http = axios.create({
  baseURL: API_BASE,
  paramsSerializer: { serialize: serializeParams },
});

http.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// On 401, refresh once (single-flight — shared with the proactive timer in AuthProvider) and retry once.
// The refresh POST lives in refreshClient.ts and uses a bare axios, so it never recurses through this interceptor.
http.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    if (error.response?.status === 401 && original && !original._retry && getRefreshToken()) {
      original._retry = true;
      try {
        const token = await refreshSingleFlight();
        original.headers.Authorization = `Bearer ${token}`;
        return await http(original);
      } catch (e) {
        onRefreshFailed(); // clears session -> <RequireAuth> redirects (doc 03)
        window.location.assign('/login');
        throw e;
      }
    }
    throw error;
  },
);

// orval mutator signature: returns the parsed body typed as T (the envelope, for shape A).
// The exported instance is `http`; orval uses `customInstance` as its mutator (CONTRACTS.md §2).
export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> =>
  http({ ...config }).then((r) => r.data as T);
